import React, { useState, useEffect } from 'react';
import { TimeEntry } from '../../types/task';
import Modal from '../common/Modal';
import { ClockIcon, CalendarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface TimeEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TimeEntryFormData) => Promise<void>;
  initialData?: TimeEntry;
  userName: string;
}

export interface TimeEntryFormData {
  description: string;
  startTime: Date;
  endTime: Date;
  isBillable: boolean;
}

export default function TimeEntryForm({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData,
  userName 
}: TimeEntryFormProps) {
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialData) {
      setDescription(initialData.description || '');
      setIsBillable(initialData.isBillable ?? true);
      
      // Start time
      const start = new Date(initialData.startTime);
      setStartDate(start.toISOString().split('T')[0]);
      setStartTime(start.toTimeString().slice(0, 5));
      
      // End time
      if (initialData.endTime) {
        const end = new Date(initialData.endTime);
        setEndDate(end.toISOString().split('T')[0]);
        setEndTime(end.toTimeString().slice(0, 5));
      }
    } else {
      // Alapértelmezett értékek (ma)
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
      setStartTime('09:00');
      setEndTime('17:00');
      setIsBillable(true);
      setDescription('');
    }
  }, [initialData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validáció
    if (!startDate || !startTime) {
      setError('Kezdés dátuma és ideje kötelező');
      return;
    }

    if (!endDate || !endTime) {
      setError('Befejezés dátuma és ideje kötelező');
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    if (endDateTime <= startDateTime) {
      setError('A befejezés időpontja későbbi kell legyen, mint a kezdés');
      return;
    }

    const duration = (endDateTime.getTime() - startDateTime.getTime()) / 1000;
    if (duration > 24 * 3600) {
      setError('Az időbejegyzés nem lehet hosszabb 24 óránál');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        description,
        startTime: startDateTime,
        endTime: endDateTime,
        isBillable
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Hiba történt a mentés során');
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = () => {
    if (!startDate || !startTime || !endDate || !endTime) return '00:00:00';
    
    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);
    
    if (end <= start) return '00:00:00';
    
    const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="lg"
      zIndex={70}
    >
      {/* Custom Header */}
      <div className="-m-6 mb-6 px-6 py-5 bg-white dark:bg-gray-800 border-b-2 border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
              <ClockIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {initialData ? t('timeTracking.editEntry') : t('timeTracking.newEntry')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {userName}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t('timeTracking.dateLabel')}
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {new Date().toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-lg p-4 shadow-sm">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Leírás */}
        <div className="space-y-2">
          <label className="flex items-center space-x-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <ClockIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span>{t('timeTracking.description')}</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('timeTracking.descriptionPlaceholder')}
            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 transition-all"
          />
        </div>

        {/* Kezdés */}
        <div className="space-y-3">
          <label className="flex items-center space-x-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <CalendarIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span>{t('timeTracking.startTime')} *</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              className="px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-500/20 dark:focus:ring-green-400/20 transition-all [color-scheme:light] dark:[color-scheme:dark]"
            />
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              className="px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-500/20 dark:focus:ring-green-400/20 transition-all [color-scheme:light] dark:[color-scheme:dark]"
            />
          </div>
        </div>

        {/* Befejezés */}
        <div className="space-y-3">
          <label className="flex items-center space-x-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <CheckCircleIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <span>{t('timeTracking.endTime')} *</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
              className="px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-500/20 dark:focus:ring-purple-400/20 transition-all [color-scheme:light] dark:[color-scheme:dark]"
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              className="px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-500/20 dark:focus:ring-purple-400/20 transition-all [color-scheme:light] dark:[color-scheme:dark]"
            />
          </div>
        </div>

        {/* Időtartam */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700/50 rounded-2xl p-5 shadow-sm dark:shadow-blue-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                {t('timeTracking.duration')}
              </span>
            </div>
            <span className="text-3xl font-mono font-bold text-blue-600 dark:text-blue-300 tracking-tight">
              {calculateDuration()}
            </span>
          </div>
        </div>

        {/* Számlázható */}
        <div className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer">
          <input
            type="checkbox"
            id="isBillable"
            checked={isBillable}
            onChange={(e) => setIsBillable(e.target.checked)}
            className="h-5 w-5 text-blue-600 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
          />
          <label htmlFor="isBillable" className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
            💰 {t('timeTracking.billableTime')}
          </label>
        </div>

        {/* Gombok */}
        <div className="flex justify-end space-x-3 pt-6 border-t-2 border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
          >
            {t('timeTracking.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 dark:from-blue-500 dark:to-indigo-500 dark:hover:from-blue-600 dark:hover:to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
          >
            {loading ? `⏳ ${t('timeTracking.saving')}` : initialData ? `✏️ ${t('timeTracking.update')}` : `✨ ${t('timeTracking.create')}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
